import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Phone, MessageCircle, Check, MapPin, Clock, Users, Car, FileText, Calculator } from 'lucide-react';
import { Button } from '../components/ui/button';
import { COMPANY_INFO } from '../mock';
import { InternalLinksSection } from '../components/InternalLinksSection';

const UnfallgutachtenPinnebergPage = () => {
  const [formData, setFormData] = useState({ name: '', phone: '', message: '' });

  const handleSubmit = async (e) => {
    e.preventDefault();
    const API_URL = process.env.REACT_APP_BACKEND_URL;
    await fetch(`${API_URL}/api/leads/submit`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...formData, source: 'unfallgutachten-pinneberg-landing-page' })
    });
    alert('Vielen Dank! Wir melden uns schnellstmöglich.');
    setFormData({ name: '', phone: '', message: '' });
  };

  const faqs = [
    {
      q: "Wie schnell bekommt man in Pinneberg einen Gutachtertermin?",
      a: "Als Ihr direkter Nachbar aus Rellingen sind wir in Pinneberg innerhalb von 5-10 Minuten. Das bedeutet: Wir können sehr kurzfristig Termine anbieten – oft noch am selben Tag, bei Notfällen innerhalb von ein bis zwei Stunden. Unser Hauptsitz in Rellingen liegt direkt an der Grenze zu Pinneberg."
    },
    {
      q: "Wer bezahlt das Gutachten bei einem Unfall in Pinneberg?",
      a: "Bei einem unverschuldeten Unfall trägt die gegnerische Haftpflichtversicherung die Gutachterkosten vollständig. Sie müssen nicht in Vorleistung gehen. Wichtig: Sie haben das gesetzliche Recht auf freie Gutachterwahl – lassen Sie sich nicht von der gegnerischen Versicherung zu deren Gutachter überreden. Bei Kaskoschäden oder Teilschuld beraten wir Sie gerne."
    },
    {
      q: "Kommt ihr auch nach Rellingen, Halstenbek oder Schenefeld?",
      a: "Selbstverständlich! Der gesamte Kreis Pinneberg ist unser Kerngebiet: Pinneberg, Rellingen (dort ist unser Hauptsitz!), Halstenbek, Schenefeld, Wedel, Uetersen, Elmshorn, Quickborn, Bönningstedt, Ellerbek, Prisdorf, Tornesch und alle weiteren Gemeinden. Wir kommen dorthin, wo Ihr Fahrzeug steht."
    },
    {
      q: "Was ist bei einem Unfall auf der A23 oder B431 zu beachten?",
      a: "Unfälle auf der A23 (Hamburg-Pinneberg-Elmshorn) oder der B431 sind für uns alltäglich. Nach einem Autobahnunfall wird Ihr Fahrzeug meist von einem Abschleppdienst geborgen. Wir können dann zur Werkstatt oder zum Abstellplatz kommen. Tipp: Machen Sie vor dem Abschleppen Fotos von der Unfallstelle und den Fahrzeugpositionen."
    },
    {
      q: "Warum ist ein unabhängiges Gutachten besser als ein Kostenvoranschlag?",
      a: "Ein Werkstatt-Kostenvoranschlag dokumentiert nur die Reparaturkosten. Ein unabhängiges Gutachten erfasst zusätzlich: Wertminderung (oft mehrere hundert Euro bei neueren Fahrzeugen), Nutzungsausfall (ca. 35-50€ pro Tag), Wiederbeschaffungswert und Restwert bei Totalschaden. Bei Schäden über 750€ zahlt sich ein Gutachten fast immer aus – die Kosten trägt die gegnerische Versicherung."
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Unfallgutachten Pinneberg | Kfz-Sachverständige vor Ort | Dein Gutachten Direkt</title>
        <meta name="description" content="Unfallgutachten in Pinneberg – Ihr Gutachter aus Rellingen, nur 5 Min. entfernt! Gerichtsfeste Gutachten im Kreis Pinneberg. Kostenlos bei unverschuldetem Unfall. ☎ Jetzt anrufen!" />
        <meta name="keywords" content="Unfallgutachten Pinneberg, Kfz Gutachter Pinneberg, Sachverständiger Pinneberg, Gutachter Rellingen, Gutachter Halstenbek, Gutachter Schenefeld, Kreis Pinneberg" />
        <link rel="canonical" href="https://www.dein-gutachten-direkt.de/unfallgutachten-pinneberg" />
        
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            "name": "Dein Gutachten Direkt - Pinneberg",
            "description": "Kfz-Sachverständigenbüro mit Hauptsitz in Rellingen – direkt an der Grenze zu Pinneberg. Über 10 Jahre Erfahrung, unabhängige Unfallgutachten im gesamten Kreis Pinneberg.",
            "telephone": "+4941018471160",
            "email": "info@dein-gutachten-direkt.de",
            "address": {
              "@type": "PostalAddress",
              "streetAddress": "Möwenstraße 2",
              "addressLocality": "Rellingen",
              "postalCode": "25462",
              "addressRegion": "Schleswig-Holstein",
              "addressCountry": "DE"
            },
            "areaServed": [
              {"@type": "City", "name": "Pinneberg"},
              {"@type": "City", "name": "Rellingen"},
              {"@type": "City", "name": "Halstenbek"},
              {"@type": "City", "name": "Schenefeld"},
              {"@type": "City", "name": "Wedel"},
              {"@type": "City", "name": "Elmshorn"},
              {"@type": "City", "name": "Uetersen"}
            ],
            "priceRange": "€€"
          })}
        </script>

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": faqs.map(faq => ({
              "@type": "Question",
              "name": faq.q,
              "acceptedAnswer": { "@type": "Answer", "text": faq.a }
            }))
          })}
        </script>
      </Helmet>

      <Header />

      <main>
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-gray-900 via-black to-gray-800 pt-28 pb-20">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-brand-green/20 border border-brand-green/30 rounded-full mb-6">
                  <MapPin className="w-4 h-4 text-brand-green" />
                  <span className="text-brand-green font-semibold text-sm">Pinneberg & Kreis Pinneberg</span>
                </div>

                <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                  Unfallgutachten
                  <br />
                  <span className="text-brand-green">in Pinneberg</span>
                </h1>
                
                <p className="text-xl text-gray-300 mb-8 leading-relaxed">
                  Wir sind Ihre <strong className="text-white">direkten Nachbarn</strong>: Unser Hauptsitz in Rellingen liegt nur 
                  <strong className="text-white"> 5 Minuten</strong> von Pinneberg entfernt. Schneller geht's nicht!
                </p>

                <div className="grid grid-cols-2 gap-4 mb-8">
                  <div className="flex items-center gap-3 text-gray-300">
                    <Check className="w-5 h-5 text-brand-green flex-shrink-0" />
                    <span>Hauptsitz in Rellingen</span>
                  </div>
                  <div className="flex items-center gap-3 text-gray-300">
                    <Check className="w-5 h-5 text-brand-green flex-shrink-0" />
                    <span>Nur 5 Min. nach Pinneberg</span>
                  </div>
                  <div className="flex items-center gap-3 text-gray-300">
                    <Check className="w-5 h-5 text-brand-green flex-shrink-0" />
                    <span>Über 10 Jahre Erfahrung</span>
                  </div>
                  <div className="flex items-center gap-3 text-gray-300">
                    <Check className="w-5 h-5 text-brand-green flex-shrink-0" />
                    <span>Gerichtsfeste Gutachten</span>
                  </div>
                  <div className="flex items-center gap-3 text-gray-300">
                    <Check className="w-5 h-5 text-brand-green flex-shrink-0" />
                    <span>Ganzer Kreis Pinneberg</span>
                  </div>
                  <div className="flex items-center gap-3 text-gray-300">
                    <Check className="w-5 h-5 text-brand-green flex-shrink-0" />
                    <span>Notfall-Termine möglich</span>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-4">
                  <a href={`tel:${COMPANY_INFO.phone}`}>
                    <Button size="lg" className="w-full sm:w-auto bg-brand-green hover:bg-brand-green-dark text-white text-lg px-8 py-6 rounded-xl font-bold">
                      <Phone className="w-5 h-5 mr-2" />
                      Jetzt anrufen
                    </Button>
                  </a>
                  <a href={`https://wa.me/${COMPANY_INFO.whatsapp}`} target="_blank" rel="noopener noreferrer">
                    <Button size="lg" variant="outline" className="w-full sm:w-auto border-2 border-white text-white hover:bg-white/10 text-lg px-8 py-6 rounded-xl font-bold">
                      <MessageCircle className="w-5 h-5 mr-2" />
                      WhatsApp
                    </Button>
                  </a>
                </div>

                <p className="text-gray-400 text-sm mt-6">
                  Bei unverschuldetem Unfall: Kostenübernahme durch gegnerische Versicherung
                </p>
              </div>

              <div className="bg-white/5 backdrop-blur border border-white/10 rounded-3xl p-8">
                <h2 className="text-2xl font-bold text-white mb-6">Ihr Vorteil: Wir sind vor Ort</h2>
                
                <div className="space-y-4 mb-8">
                  <div className="flex items-start gap-4">
                    <MapPin className="w-6 h-6 text-brand-green flex-shrink-0 mt-1" />
                    <div>
                      <p className="text-white font-semibold">Unser Standort</p>
                      <p className="text-gray-400">Möwenstraße 2, 25462 Rellingen</p>
                      <p className="text-gray-400">Direkt an der Grenze zu Pinneberg</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <Clock className="w-6 h-6 text-brand-green flex-shrink-0 mt-1" />
                    <div>
                      <p className="text-white font-semibold">Kürzeste Anfahrt</p>
                      <p className="text-gray-400">5 Min. nach Pinneberg-Zentrum</p>
                      <p className="text-gray-400">Notfall-Termine innerhalb 1-2 Stunden</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <Users className="w-6 h-6 text-brand-green flex-shrink-0 mt-1" />
                    <div>
                      <p className="text-white font-semibold">Lokale Expertise</p>
                      <p className="text-gray-400">Wir kennen jeden Winkel des Kreises</p>
                      <p className="text-gray-400">Netzwerk mit lokalen Werkstätten</p>
                    </div>
                  </div>
                </div>

                <div className="bg-brand-green/20 border border-brand-green/30 rounded-xl p-4">
                  <p className="text-brand-green font-semibold text-sm">
                    <strong>Freie Gutachterwahl:</strong> Bei unverschuldetem Unfall dürfen Sie Ihren Gutachter selbst wählen. Die gegnerische Versicherung muss die Kosten tragen.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Über uns Section */}
        <section className="py-20 bg-white">
          <div className="max-w-4xl mx-auto px-6">
            <div className="text-center mb-12">
              <span className="text-brand-green font-semibold text-sm uppercase tracking-wider">Über uns</span>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-2">Ihr Kfz-Gutachter im Kreis Pinneberg</h2>
            </div>

            <div className="prose prose-lg max-w-none">
              <p className="text-gray-700 leading-relaxed text-lg">
                <strong>Dein Gutachten Direkt</strong> ist Ihr lokaler Kfz-Sachverständiger mit Hauptsitz in <strong>Rellingen</strong> – 
                direkt an der Grenze zu Pinneberg. Näher an Pinneberg geht es kaum! In nur 5 Minuten sind wir bei Ihnen.
              </p>

              <p className="text-gray-700 leading-relaxed text-lg">
                Seit über <strong>10 Jahren</strong> erstellen wir unabhängige Unfallgutachten für Geschädigte im Kreis Pinneberg. 
                Ob Auffahrunfall in der Pinneberger Innenstadt, Parkrempler beim Einkaufszentrum oder Wildunfall auf der Landesstraße – 
                wir dokumentieren jeden Schaden <strong>gerichtsfest</strong> und kennen die Region wie unsere Westentasche.
              </p>

              <p className="text-gray-700 leading-relaxed text-lg">
                Als Teil der lokalen Gemeinschaft haben wir über die Jahre ein Netzwerk aufgebaut: Wir kennen die zuverlässigen Werkstätten 
                in Pinneberg und arbeiten eng mit <strong>Fachanwälten für Verkehrsrecht</strong> in der Region zusammen.
              </p>

              <div className="bg-gray-50 rounded-2xl p-8 my-8">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Das zeichnet uns aus:</h3>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-brand-green flex-shrink-0 mt-1" />
                    <span><strong>Vor Ort:</strong> Hauptsitz in Rellingen – nur 5 Minuten von Pinneberg entfernt</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-brand-green flex-shrink-0 mt-1" />
                    <span><strong>Unabhängig:</strong> Wir arbeiten ausschließlich für Sie – nicht für Versicherungen</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-brand-green flex-shrink-0 mt-1" />
                    <span><strong>Erfahren:</strong> Über 10 Jahre Erfahrung im Kreis Pinneberg</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-brand-green flex-shrink-0 mt-1" />
                    <span><strong>Gerichtsfest:</strong> Unsere Gutachten sind rechtssicher und vor Gericht verwendbar</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-brand-green flex-shrink-0 mt-1" />
                    <span><strong>Schnell:</strong> In Pinneberg oft am selben Tag, Notfälle innerhalb 1-2 Stunden</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Was beinhaltet unser Gutachten */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-4xl mx-auto px-6">
            <div className="text-center mb-12">
              <span className="text-brand-green font-semibold text-sm uppercase tracking-wider">Leistungen</span>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-2">Was beinhaltet unser Gutachten?</h2>
              <p className="text-xl text-gray-600 mt-4">Alle Ansprüche sichern – nicht nur die Reparaturkosten</p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white rounded-2xl p-6 border-2 border-gray-200 hover:border-brand-green/50 transition-colors">
                <div className="flex items-center gap-3 mb-3">
                  <Calculator className="w-6 h-6 text-brand-green" />
                  <h3 className="font-bold text-gray-900 text-lg">Reparaturkostenkalkulation</h3>
                </div>
                <p className="text-gray-700">Detaillierte Berechnung nach Herstellervorgaben: Arbeitslohn, Original-Ersatzteile, Lackierung, Achsvermessung und alle weiteren Nebenkosten.</p>
              </div>
              
              <div className="bg-white rounded-2xl p-6 border-2 border-gray-200 hover:border-brand-green/50 transition-colors">
                <div className="flex items-center gap-3 mb-3">
                  <Car className="w-6 h-6 text-brand-green" />
                  <h3 className="font-bold text-gray-900 text-lg">Wertminderung</h3>
                </div>
                <p className="text-gray-700">Der merkantile Minderwert nach dem Unfall. Bei einem 3 Jahre alten Fahrzeug mit 3.000€ Reparaturkosten oft 300-600€ zusätzlich.</p>
              </div>
              
              <div className="bg-white rounded-2xl p-6 border-2 border-gray-200 hover:border-brand-green/50 transition-colors">
                <div className="flex items-center gap-3 mb-3">
                  <Clock className="w-6 h-6 text-brand-green" />
                  <h3 className="font-bold text-gray-900 text-lg">Nutzungsausfall</h3>
                </div>
                <p className="text-gray-700">Für jeden Tag ohne Auto: 23€ (Kleinwagen) bis 175€ (Oberklasse). Bei 10 Tagen Werkstattzeit summiert sich das schnell auf 350-500€.</p>
              </div>
              
              <div className="bg-white rounded-2xl p-6 border-2 border-gray-200 hover:border-brand-green/50 transition-colors">
                <div className="flex items-center gap-3 mb-3">
                  <FileText className="w-6 h-6 text-brand-green" />
                  <h3 className="font-bold text-gray-900 text-lg">Wiederbeschaffungswert</h3>
                </div>
                <p className="text-gray-700">Bei Totalschaden: Was kostet ein gleichwertiges Ersatzfahrzeug im lokalen Markt? Wir recherchieren aktuelle Angebote in der Region.</p>
              </div>
              
              <div className="bg-white rounded-2xl p-6 border-2 border-gray-200 hover:border-brand-green/50 transition-colors">
                <div className="flex items-center gap-3 mb-3">
                  <Calculator className="w-6 h-6 text-brand-green" />
                  <h3 className="font-bold text-gray-900 text-lg">Restwertermittlung</h3>
                </div>
                <p className="text-gray-700">Der Verkaufswert des beschädigten Fahrzeugs. Wir holen mehrere Angebote ein, um den besten Preis für Sie zu ermitteln.</p>
              </div>
              
              <div className="bg-white rounded-2xl p-6 border-2 border-gray-200 hover:border-brand-green/50 transition-colors">
                <div className="flex items-center gap-3 mb-3">
                  <FileText className="w-6 h-6 text-brand-green" />
                  <h3 className="font-bold text-gray-900 text-lg">Fotodokumentation</h3>
                </div>
                <p className="text-gray-700">30-50 professionelle Fotos dokumentieren jeden Kratzer. Diese Beweissicherung ist vor Gericht entscheidend wichtig.</p>
              </div>
            </div>

            <div className="bg-brand-green/10 border-2 border-brand-green/30 rounded-2xl p-6 mt-8 text-center">
              <p className="text-gray-900 font-semibold">
                <strong>Rechenbeispiel:</strong> Ein Mittelklassewagen mit 4.000€ Reparaturkosten, 400€ Wertminderung und 10 Tagen Nutzungsausfall (450€) 
                = 850€ mehr als nur der Kostenvoranschlag. Das Gutachten zahlt die gegnerische Versicherung!
              </p>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-20 bg-white">
          <div className="max-w-4xl mx-auto px-6">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Häufige Fragen zu Unfallgutachten in Pinneberg</h2>
            <div className="space-y-4">
              {faqs.map((faq, idx) => (
                <div key={idx} className="bg-gray-50 rounded-2xl p-6 border border-gray-100">
                  <h3 className="font-bold text-gray-900 mb-3 text-lg">{faq.q}</h3>
                  <p className="text-gray-700 leading-relaxed">{faq.a}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Stadtteile */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Der gesamte Kreis Pinneberg</h2>
            <p className="text-lg text-gray-600 mb-8">Von unserem Hauptsitz in Rellingen erreichen wir alle Gemeinden des Kreises schnell und unkompliziert</p>
            <div className="flex flex-wrap justify-center gap-3">
              {['Pinneberg', 'Rellingen', 'Halstenbek', 'Schenefeld', 'Wedel', 'Elmshorn', 'Uetersen', 'Tornesch', 'Quickborn', 'Bönningstedt', 'Ellerbek', 'Prisdorf', 'Kummerfeld', 'Appen', 'Moorrege', 'Haseldorf', 'Heist', 'Holm'].map((ort) => (
                <span key={ort} className="px-4 py-2 bg-white rounded-lg border border-gray-200 text-gray-700 font-medium hover:border-brand-green/50 transition-colors">
                  {ort}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 bg-gradient-to-br from-gray-900 to-black">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <h2 className="text-3xl font-bold text-white mb-6">Jetzt Gutachten in Pinneberg beauftragen</h2>
            <p className="text-xl text-gray-300 mb-8">Ihr Nachbar aus Rellingen • In 5 Min. bei Ihnen</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href={`tel:${COMPANY_INFO.phone}`}>
                <Button size="lg" className="bg-brand-green hover:bg-brand-green-dark text-white text-lg px-10 py-6 rounded-xl font-bold">
                  <Phone className="w-5 h-5 mr-2" />
                  04101 847 11 60
                </Button>
              </a>
              <a href={`https://wa.me/${COMPANY_INFO.whatsapp}`} target="_blank" rel="noopener noreferrer">
                <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white/10 text-lg px-10 py-6 rounded-xl font-bold">
                  <MessageCircle className="w-5 h-5 mr-2" />
                  WhatsApp
                </Button>
              </a>
            </div>
          </div>
        </section>

        <InternalLinksSection currentPage="/unfallgutachten-pinneberg" />
      </main>

      <Footer />
    </div>
  );
};

export default UnfallgutachtenPinnebergPage;
